// SD28 / FR-M5 / FR-X4 wire shapes. Keep in lockstep with server/src/live.rs
// and clients/extension/src/live/protocol.ts.
import type { DiarizationStatus, SpeakerOut, TranscriptSegmentOut } from "../api/types";

export type LiveSessionStatus =
  | "live"
  | "paused"
  | "degraded"
  | "finalizing"
  | "ready"
  | "unavailable"
  | "failed";

export interface TranscriptLine {
  seq: number;
  t_start_ms: number;
  t_end_ms: number;
  speaker_label: string;
  speaker_id: string | null;
  text: string;
  is_final: boolean;
}

export interface LiveBookmark {
  id: string;
  at_ms: number;
  label: string;
  created_at: string;
}

export interface LiveSession {
  id: string;
  org_id: string;
  grackle_id: string | null;
  status: LiveSessionStatus;
  provider: "stub" | "personal" | "organization" | "hosted" | "unavailable" | string;
  model_id: string | null;
  provenance: string | null;
  degrade_reason: string | null;
  title: string;
  capture_platform?: "zoom_desktop" | "teams_desktop" | null;
  started_at: string;
  ended_at: string | null;
  message: string | null;
  lines: TranscriptLine[];
  bookmarks: LiveBookmark[];
}

export interface GrackleTranscript {
  status: string;
  text?: string | null;
  vtt?: string | null;
  language?: string | null;
  model_id?: string | null;
  provenance?: string | null;
  error?: string | null;
  lines?: TranscriptLine[] | null;
  bookmarks?: LiveBookmark[] | null;
  live_status?: string | null;
  /** F4: display label for the provenance line ("Orchardrun" / an org account's label). */
  provider_label?: string | null;
  /** F4: timed segments with stable ids and per-recording speaker names. */
  segments?: TranscriptSegmentOut[] | null;
  /** FR-M5a: the editor's speaker names in force (provider tag → name). */
  speaker_names?: Record<string, string> | null;
  /** FR-M5b: merged tag → target tag. */
  speaker_merges?: Record<string, string> | null;
  /** FR-M5b: every diarized speaker with its own name and merge target. */
  speakers?: SpeakerOut[] | null;
  /** FR-M5b: notes exist and predate the last merge or unmerge. */
  notes_predate_merge?: boolean | null;
  diarization_status?: DiarizationStatus | null;
  /** F4: why speakers are missing, when they are. */
  diarization_error?: string | null;
}

export interface LiveStatusMessage {
  type: "live_status";
  session_id: string;
  status: LiveSessionStatus | string;
  degrade_reason?: string;
  message?: string;
}

export interface TranscriptPush {
  type: "transcript";
  session_id: string;
  line: TranscriptLine;
}

/**
 * F17 §3: the post-call (F4) transcript landed. Replaces the provisional live lines. Unlike
 * live lines these carry segment ids and diarized "Speaker 1…N" labels.
 */
export interface FinalTranscriptLine {
  id: string;
  t_start_ms: number;
  t_end_ms: number;
  speaker_label: string | null;
  text: string;
}

export interface TranscriptFinalPush {
  type: "transcript.final";
  session_id: string;
  grackle_id: string;
  lines: FinalTranscriptLine[];
}

export type LiveWsMessage = LiveStatusMessage | TranscriptPush | TranscriptFinalPush;

export const SPEAKER_COLORS = [
  "oklch(0.52 0.10 300)",
  "oklch(0.50 0.09 200)",
  "oklch(0.48 0.10 150)",
  "oklch(0.54 0.10 40)",
  "oklch(0.46 0.09 262)",
  "oklch(0.50 0.08 100)",
];

function labelHash(label: string): number {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return h;
}

export function speakerColor(label: string): string {
  return SPEAKER_COLORS[labelHash(label) % SPEAKER_COLORS.length] ?? SPEAKER_COLORS[0];
}

/** The DS speaker slot (--speaker-1 … --speaker-5) a live label always lands on. */
export function speakerSlot(label: string): 1 | 2 | 3 | 4 | 5 {
  return ((labelHash(label) % 5) + 1) as 1 | 2 | 3 | 4 | 5;
}

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function formatLineClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
